/**
 * TemplatePanel.js
 *
 * Wiris-style clickable template palette for CKEditor5, covering:
 * Basic, Calculus, Trigonometry, Sets & logic, Statistics,
 * Matrices & vectors (with a resizable NxN matrix builder, up to 8x8),
 * Greek letters, and four chemistry categories (formulas, reactions,
 * nuclear, physical chemistry).
 *
 * REDESIGNED as a true centered POPUP (fixed-position overlay + modal
 * box appended to document.body) instead of a toolbar-anchored dropdown.
 * This is a bigger structural change from the previous version:
 *   - No more createDropdown/DropdownView/RawHtmlView at all -- the
 *     toolbar button is now a plain ButtonView that just opens/closes
 *     our own modal, so there's no fighting CKEditor's dropdown
 *     positioning logic (which is why it always appeared awkwardly
 *     anchored under the toolbar button before).
 *   - New color scheme: indigo/violet accent (#4f46e5) instead of the
 *     previous blue (#1a73e8), with a dark semi-transparent backdrop.
 *
 * Clicking a plain template populates the free-type field (does not
 * insert). Clicking Insert/Update actually writes to the document via
 * the self-built 'insertMathEquation' command (see ../plugins/mathequation/).
 * Clicking an EXISTING equation in the document reopens this same modal,
 * pre-filled, in "Update" mode.
 *
 * Add to CustomEditorBuild.js (already done):
 *   import TemplatePanel from '../plugins/TemplatePanel';
 *   builtinPlugins: [..., MathEquation, TemplatePanel]
 *   toolbar items: [..., 'templatePanel', ...]
 */
import { Plugin, ButtonView } from 'ckeditor5';
import katex from 'katex';
import { MathMLToLaTeX } from 'mathml-to-latex';

// -- Color scheme (change these two lines to retheme the whole popup) --
const ACCENT = '#4f46e5';
const ACCENT_HOVER = '#4338ca';
const ACCENT_LIGHT = '#eef2ff';

const CATEGORIES = {
  Basic: [
    ['\\frac{a}{b}', 'Fraction'],
    ['x^{n}', 'Power'],
    ['x_{n}', 'Subscript'],
    ['\\sqrt{x}', 'Square root'],
    ['\\sqrt[n]{x}', 'Nth root'],
    ['|x|', 'Absolute value'],
    ['\\lfloor x \\rfloor', 'Floor'],
    ['\\lceil x \\rceil', 'Ceiling'],
    ['\\frac{-b \\pm \\sqrt{b^2-4ac}}{2a}', 'Quadratic formula'],
    ['n!', 'Factorial'],
    ['a\\%', 'Percent'],
    ['e^{x}', 'Exponential'],
    ['\\log_{b}(x)', 'Logarithm'],
    ['\\ln(x)', 'Natural log'],
    ['a \\ne b', 'Not equal'],
    ['a \\le b', 'Less or equal'],
    ['a \\ge b', 'Greater or equal'],
    ['a \\approx b', 'Approximately'],
    ['a \\propto b', 'Proportional to'],
    ['\\pm', 'Plus-minus'],
  ],
  Calculus: [
    ['\\frac{d}{dx}', 'Derivative'],
    ['\\frac{d^2}{dx^2}', 'Second derivative'],
    ['\\frac{\\partial}{\\partial x}', 'Partial derivative'],
    ['\\int_{a}^{b}', 'Definite integral'],
    ['\\int', 'Indefinite integral'],
    ['\\iint', 'Double integral'],
    ['\\iiint', 'Triple integral'],
    ['\\oint', 'Contour integral'],
    ['\\lim_{x \\to a}', 'Limit'],
    ['\\sum_{i=1}^{n}', 'Sum'],
    ['\\prod_{i=1}^{n}', 'Product'],
    ['\\infty', 'Infinity'],
    ['\\nabla', 'Gradient (nabla)'],
    ['\\nabla \\cdot \\vec{F}', 'Divergence'],
    ['\\nabla \\times \\vec{F}', 'Curl'],
    ["f'(x)", 'First derivative'],
    ["f''(x)", 'Second derivative'],
    ['\\frac{dy}{dx} = ky', 'Simple ODE'],
  ],
  Trigonometry: [
    ['\\sin(x)', 'sin'],
    ['\\cos(x)', 'cos'],
    ['\\tan(x)', 'tan'],
    ['\\csc(x)', 'csc'],
    ['\\sec(x)', 'sec'],
    ['\\cot(x)', 'cot'],
    ['\\sin^{-1}(x)', 'arcsin'],
    ['\\cos^{-1}(x)', 'arccos'],
    ['\\tan^{-1}(x)', 'arctan'],
    ['\\sinh(x)', 'sinh'],
    ['\\cosh(x)', 'cosh'],
    ['\\tanh(x)', 'tanh'],
    ['\\sin^2(x)+\\cos^2(x)=1', 'Pythagorean identity'],
    ['\\sin(2x)=2\\sin x\\cos x', 'Double angle'],
  ],
  'Sets & logic': [
    ['A \\cup B', 'Union'],
    ['A \\cap B', 'Intersection'],
    ['A \\subset B', 'Subset'],
    ['A \\subseteq B', 'Subset or equal'],
    ['A \\supset B', 'Superset'],
    ['A \\not\\subset B', 'Not a subset'],
    ['x \\in A', 'Element of'],
    ['x \\notin A', 'Not an element of'],
    ['\\forall x', 'For all'],
    ['\\exists x', 'Exists'],
    ['\\nexists x', 'Does not exist'],
    ['p \\implies q', 'Implies'],
    ['p \\iff q', 'Iff'],
    ['\\emptyset', 'Empty set'],
    ['A \\setminus B', 'Set difference'],
    ['A \\triangle B', 'Symmetric difference'],
    ['A^c', 'Complement'],
    ['|A|', 'Cardinality'],
    ['\\mathcal{P}(A)', 'Power set'],
    ['p \\lor q', 'Logical OR'],
    ['p \\land q', 'Logical AND'],
    ['\\neg p', 'Logical NOT'],
    ['p \\oplus q', 'XOR'],
    ['\\therefore', 'Therefore'],
    ['\\because', 'Because'],
    ['A \\cong B', 'Congruent'],
    ['A \\sim B', 'Similar'],
  ],
  Statistics: [
    ['\\bar{x}', 'Mean'],
    ['\\binom{n}{k}', 'Binomial coeff'],
    ['\\sigma', 'Std deviation'],
    ['\\sigma^2', 'Variance'],
    ['P(A|B)', 'Conditional prob'],
    ['E[X]', 'Expected value'],
    ['{}^nP_r', 'Permutation'],
    ['{}^nC_r', 'Combination'],
    ['z = \\frac{x-\\mu}{\\sigma}', 'Z-score'],
    ['\\rho', 'Correlation coeff'],
    ['\\chi^2', 'Chi-square'],
  ],
  'Matrices & vectors': {
    special: 'matrix',
    templates: [
      ['\\vec{v}', 'Vector'],
      ['\\vec{a} \\cdot \\vec{b}', 'Dot product'],
      ['\\vec{a} \\times \\vec{b}', 'Cross product'],
      ['|\\vec{v}|', 'Magnitude'],
      ['\\hat{i}', 'Unit vector i'],
      ['\\hat{j}', 'Unit vector j'],
      ['\\hat{k}', 'Unit vector k'],
      ['A^{T}', 'Transpose'],
      ['A^{-1}', 'Inverse'],
      ['I_n', 'Identity matrix'],
      ['\\det(A)', 'Determinant'],
      ['\\begin{vmatrix}a & b\\\\ c & d\\end{vmatrix}', '2x2 determinant'],
    ],
  },
  Greek: [
    ['\\alpha', 'alpha'], ['\\beta', 'beta'], ['\\gamma', 'gamma'], ['\\delta', 'delta'],
    ['\\epsilon', 'epsilon'], ['\\zeta', 'zeta'], ['\\eta', 'eta'], ['\\theta', 'theta'],
    ['\\iota', 'iota'], ['\\kappa', 'kappa'], ['\\lambda', 'lambda'], ['\\mu', 'mu'],
    ['\\nu', 'nu'], ['\\xi', 'xi'], ['\\pi', 'pi'], ['\\rho', 'rho'],
    ['\\sigma', 'sigma'], ['\\tau', 'tau'], ['\\phi', 'phi'], ['\\chi', 'chi'],
    ['\\psi', 'psi'], ['\\omega', 'omega'],
    ['\\Gamma', 'Gamma'], ['\\Delta', 'Delta'], ['\\Theta', 'Theta'], ['\\Lambda', 'Lambda'],
    ['\\Xi', 'Xi'], ['\\Pi', 'Pi'], ['\\Sigma', 'Sigma'], ['\\Phi', 'Phi'],
    ['\\Psi', 'Psi'], ['\\Omega', 'Omega'],
  ],
  'Chemistry: formulas': [
    ['\\ce{H2O}', 'Water'],
    ['\\ce{H2SO4}', 'Sulfuric acid'],
    ['\\ce{CO2}', 'Carbon dioxide'],
    ['\\ce{NH3}', 'Ammonia'],
    ['\\ce{CH4}', 'Methane'],
    ['\\ce{C6H12O6}', 'Glucose'],
    ['\\ce{C2H5OH}', 'Ethanol'],
    ['\\ce{CH3COOH}', 'Acetic acid'],
    ['\\ce{CaCO3}', 'Calcium carbonate'],
    ['\\ce{NaOH}', 'Sodium hydroxide'],
    ['\\ce{HCl}', 'Hydrochloric acid'],
    ['\\ce{Na+ + Cl- -> NaCl}', 'Ionic formula'],
    ['\\ce{H2O(l)}', 'State: liquid'],
    ['\\ce{O2(g)}', 'State: gas'],
    ['\\ce{NaCl(aq)}', 'State: aqueous'],
    ['\\ce{Fe(s)}', 'State: solid'],
    ['\\ce{[Cu(NH3)4]^2+}', 'Complex ion'],
    ['\\ce{CuSO4.5H2O}', 'Hydrate'],
  ],
  'Chemistry: reactions': [
    ['\\ce{2H2 + O2 -> 2H2O}', 'Synthesis'],
    ['\\ce{CaCO3 ->[\\Delta] CaO + CO2}', 'Thermal decomposition'],
    ['\\ce{CH4 + 2O2 -> CO2 + 2H2O}', 'Combustion'],
    ['\\ce{AgNO3 + NaCl -> AgCl v + NaNO3}', 'Precipitation'],
    ['\\ce{HCl + NaOH -> NaCl + H2O}', 'Acid-base'],
    ['\\ce{A + B <=> C + D}', 'Equilibrium'],
    ['\\ce{Zn -> Zn^2+ + 2e-}', 'Oxidation (redox)'],
    ['\\ce{Cu^2+ + 2e- -> Cu}', 'Reduction (redox)'],
    ['\\ce{Zn + CuSO4 -> ZnSO4 + Cu}', 'Single displacement'],
    ['\\ce{BaCl2 + Na2SO4 -> BaSO4 v + 2NaCl}', 'Double displacement'],
    ['\\ce{CH3COOH + C2H5OH ->[H2SO4] CH3COOC2H5 + H2O}', 'Esterification'],
    ['\\ce{A + B ->[catalyst] C}', 'Catalyzed reaction'],
  ],
  'Chemistry: nuclear': [
    ['\\ce{^{14}_6C}', 'Isotope notation'],
    ['\\ce{^{238}_{92}U -> ^{234}_{90}Th + ^{4}_{2}He}', 'Alpha decay'],
    ['\\ce{^{14}_6C -> ^{14}_7N + e-}', 'Beta decay'],
    ['\\ce{^{1}_{0}n}', 'Neutron'],
    ['\\ce{^{60}_{27}Co -> ^{60}_{27}Co + \\gamma}', 'Gamma emission'],
    ['\\ce{^{22}_{11}Na -> ^{22}_{10}Ne + e+}', 'Positron emission'],
    ['t_{1/2}', 'Half-life'],
  ],
  'Chemistry: physical': [
    ['K_{eq}', 'Equilibrium constant'],
    ['\\text{pH} = -\\log[\\ce{H+}]', 'pH formula'],
    ['\\Delta H', 'Enthalpy change'],
    ['\\Delta S', 'Entropy change'],
    ['\\Delta G', 'Gibbs free energy'],
    ['k = Ae^{-E_a/RT}', 'Arrhenius equation'],
    ['\\ce{[A]}', 'Concentration'],
    ['M = \\frac{n}{V}', 'Molarity'],
    ['PV = nRT', 'Ideal gas law'],
    ['\\text{rate} = k[A]^m[B]^n', 'Rate law'],
  ],
};
function generateMatrix(rows, cols, bracket) {
  const lines = [];
  for (let r = 1; r <= rows; r++) {
    const cells = [];
    for (let c = 1; c <= cols; c++) cells.push(`a_{${r}${c}}`);
    lines.push(cells.join(' & '));
  }
  return `\\begin{${bracket}}${lines.join(' \\\\ ')}\\end{${bracket}}`;
}

function insertEquation(editor, tex, elementToUpdate) {
  editor.execute('insertMathEquation', { latex: tex, elementToUpdate });
  editor.editing.view.focus();
}

// Best-effort starting point when a user clicks an old Wiris/MathType
// equation -- covers common structures (fractions, powers, roots) well
// enough to save retyping, but is never trusted blindly: the user always
// sees it in the free-type field and must review/edit before saving.
function guessLatexFromMathML(mathml) {
  try {
    return MathMLToLaTeX.convert(mathml) || '';
  } catch (e) {
    return '';
  }
}

function buildLegacyNotice() {
  const notice = document.createElement('div');
  notice.style.cssText =
    'display:none;padding:10px 18px;background:#fffbeb;border-bottom:1px solid #fde68a;' +
    'color:#92400e;font-size:12px;flex-shrink:0;';
  notice.textContent =
    'Converted from an old equation -- double-check the LaTeX below before saving.';
  return notice;
}

function buildFreeInputRow(onInsert) {
  const wrap = document.createElement('div');
  wrap.style.cssText = 'display:flex;flex-direction:column;gap:8px;padding:14px 18px;border-bottom:1px solid #e2e8f0;flex-shrink:0;';

  const input = document.createElement('input');
  input.type = 'text';
  input.placeholder = 'Type LaTeX directly, e.g. \\ce{2H2 + O2 -> 2H2O}';
  input.style.cssText = 'width:100%;font-family:monospace;padding:8px;box-sizing:border-box;border:1px solid #cbd5e1;border-radius:6px;';
  input.addEventListener('focus', () => { input.style.borderColor = ACCENT; });
  input.addEventListener('blur', () => { input.style.borderColor = '#cbd5e1'; });

  const preview = document.createElement('div');
  preview.style.cssText =
    'min-height:48px;padding:10px 8px;background:#f8fafc;border-radius:6px;' +
    'display:flex;align-items:center;overflow-x:auto;overflow-y:hidden;border:1px solid #e2e8f0;';

  const renderPreview = () => {
    preview.innerHTML = '';
    try {
      katex.render(input.value || ' ', preview, { throwOnError: false });
    } catch (e) {
      preview.textContent = 'Could not parse';
    }
  };
  input.addEventListener('input', renderPreview);
  renderPreview();

  // Remembers where the caret/selection was in the input so a template
  // click can insert there instead of clobbering whatever was already
  // typed (e.g. a converted legacy equation). Clicking a template button
  // moves focus off the input without firing any event on it, so this has
  // to be captured continuously while the user interacts with the input,
  // not read at click time.
  let caretStart = input.value.length;
  let caretEnd = input.value.length;
  const trackCaret = () => {
    caretStart = input.selectionStart ?? input.value.length;
    caretEnd = input.selectionEnd ?? caretStart;
  };
  input.addEventListener('keyup', trackCaret);
  input.addEventListener('click', trackCaret);
  input.addEventListener('select', trackCaret);
  input.addEventListener('input', trackCaret);

  const insertAtCaret = (text) => {
    const start = Math.min(caretStart, input.value.length);
    const end = Math.min(Math.max(caretEnd, start), input.value.length);
    input.value = input.value.slice(0, start) + text + input.value.slice(end);
    const newPos = start + text.length;
    caretStart = caretEnd = newPos;
    input.focus();
    input.setSelectionRange(newPos, newPos);
    renderPreview();
  };

  // For whole-field replacements (opening the modal fresh, loading an
  // existing equation for edit, closing) rather than a template insert --
  // resets the caret to the end so a template click right after still
  // lands somewhere sane instead of at a stale position from before.
  const setValue = (text) => {
    input.value = text;
    caretStart = caretEnd = input.value.length;
    renderPreview();
  };

  const insertBtn = document.createElement('button');
  insertBtn.type = 'button';
  insertBtn.textContent = 'Insert';
  insertBtn.style.cssText =
    `align-self:flex-start;padding:8px 22px;cursor:pointer;` +
    `background:${ACCENT};color:#fff;border:none;border-radius:6px;` +
    `font-weight:600;font-size:13px;`;
  insertBtn.addEventListener('mouseenter', () => { insertBtn.style.background = ACCENT_HOVER; });
  insertBtn.addEventListener('mouseleave', () => { insertBtn.style.background = ACCENT; });
  insertBtn.addEventListener('click', () => {
    if (input.value) onInsert(input.value);
  });

  wrap.append(input, preview, insertBtn);
  return { element: wrap, input, insertBtn, renderPreview, insertAtCaret, setValue };
}

function buildTemplateButton(tex, label, onPick) {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.title = label;
  btn.style.cssText =
    'border:1px solid #e2e8f0;border-radius:6px;padding:6px 4px;cursor:pointer;background:#fff;' +
    'display:flex;flex-direction:column;align-items:center;justify-content:center;gap:4px;' +
    'height:84px;overflow:hidden;min-width:90px;transition:border-color .15s,background .15s;';
  btn.addEventListener('mouseenter', () => { btn.style.borderColor = ACCENT; btn.style.background = ACCENT_LIGHT; });
  btn.addEventListener('mouseleave', () => { btn.style.borderColor = '#e2e8f0'; btn.style.background = '#fff'; });

  const wrap = document.createElement('div');
  wrap.className = 'tp-eq-wrap';
  wrap.style.cssText =
    'max-width:100%;max-height:56px;white-space:nowrap;transform-origin:center;' +
    'display:flex;align-items:center;justify-content:center;';
  try {
    katex.render(tex, wrap, { throwOnError: false });
  } catch (e) {
    wrap.textContent = tex;
  }

  const cap = document.createElement('span');
  cap.textContent = label;
  cap.style.cssText =
    'font-size:11px;color:#64748b;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:100%;';

  btn.append(wrap, cap);
  btn.addEventListener('click', () => onPick(tex));
  return btn;
}

// Long chemistry/nuclear equations render wider than their card at full
// size, and tall content (fractions, roots, derivatives) can be taller
// than the card -- without checking BOTH dimensions, content that "fits"
// by width alone still gets clipped top/bottom by overflow:hidden.
function fitTemplateButtons(container) {
  container.querySelectorAll('.tp-eq-wrap').forEach((wrap) => {
    wrap.style.transform = 'none';
    const btn = wrap.closest('button');
    if (!btn) return;

    const maxWidth = btn.clientWidth - 12;
    const maxHeight = 56;

    const contentWidth = wrap.scrollWidth;
    const contentHeight = wrap.scrollHeight;

    const widthRatio = contentWidth > maxWidth ? maxWidth / contentWidth : 1;
    const heightRatio = contentHeight > maxHeight ? maxHeight / contentHeight : 1;
    const scale = Math.max(0.4, Math.min(widthRatio, heightRatio));

    if (scale < 1) {
      wrap.style.transform = `scale(${scale})`;
    }
  });
}

function buildMatrixBuilder(onGenerate) {
  const wrap = document.createElement('div');
  wrap.style.cssText =
    'grid-column:1/-1;display:flex;gap:8px;align-items:center;flex-wrap:wrap;padding:8px;background:#f8fafc;border-radius:6px;margin:4px 0;border:1px solid #e2e8f0;';

  const rowsInput = document.createElement('input');
  rowsInput.type = 'number';
  rowsInput.min = 1;
  rowsInput.max = 8;
  rowsInput.value = 3;
  rowsInput.style.cssText = 'width:48px;padding:4px;border:1px solid #cbd5e1;border-radius:4px;';

  const colsInput = document.createElement('input');
  colsInput.type = 'number';
  colsInput.min = 1;
  colsInput.max = 8;
  colsInput.value = 3;
  colsInput.style.cssText = 'width:48px;padding:4px;border:1px solid #cbd5e1;border-radius:4px;';

  const bracketSelect = document.createElement('select');
  bracketSelect.style.cssText = 'padding:4px;border:1px solid #cbd5e1;border-radius:4px;';
  [
    ['pmatrix', '( )'],
    ['bmatrix', '[ ]'],
    ['Bmatrix', '{ }'],
    ['vmatrix', '| | (det)'],
  ].forEach(([value, label]) => {
    const opt = document.createElement('option');
    opt.value = value;
    opt.textContent = label;
    bracketSelect.appendChild(opt);
  });

  const genBtn = document.createElement('button');
  genBtn.type = 'button';
  genBtn.textContent = 'Generate';
  genBtn.style.cssText =
    `padding:6px 14px;cursor:pointer;background:${ACCENT};color:#fff;` +
    `border:none;border-radius:4px;font-weight:600;font-size:12px;`;
  genBtn.addEventListener('click', () => {
    const rows = Math.min(8, Math.max(1, parseInt(rowsInput.value, 10) || 3));
    const cols = Math.min(8, Math.max(1, parseInt(colsInput.value, 10) || 3));
    onGenerate(generateMatrix(rows, cols, bracketSelect.value));
  });

  const rowsLabel = document.createElement('label');
  rowsLabel.textContent = 'Rows ';
  rowsLabel.style.fontSize = '12px';
  rowsLabel.appendChild(rowsInput);

  const colsLabel = document.createElement('label');
  colsLabel.textContent = 'Cols ';
  colsLabel.style.fontSize = '12px';
  colsLabel.appendChild(colsInput);

  wrap.append(rowsLabel, colsLabel, bracketSelect, genBtn);
  return wrap;
}

export default class TemplatePanel extends Plugin {
  static get pluginName() {
    return 'TemplatePanel';
  }

  init() {
    const editor = this.editor;
    let editingElement = null;

    // ---- Build the modal (overlay + box) once, appended to <body> ----
    const overlay = document.createElement('div');
    overlay.style.cssText =
      'position:fixed;inset:0;background:rgba(15,23,42,0.55);display:none;' +
      'align-items:center;justify-content:center;z-index:9999;';

    const modal = document.createElement('div');
    modal.style.cssText =
      'background:#fff;border-radius:10px;width:460px;max-height:82vh;' +
      'display:flex;flex-direction:column;overflow:hidden;' +
      'box-shadow:0 20px 60px rgba(0,0,0,0.35);font-family:sans-serif;';

    const header = document.createElement('div');
    header.style.cssText =
      `display:flex;align-items:center;justify-content:space-between;` +
      `padding:14px 18px;background:${ACCENT};color:#fff;flex-shrink:0;`;
    const title = document.createElement('strong');
    title.textContent = 'Math / Chemistry equation';
    title.style.fontSize = '14px';
    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.innerHTML = '&times;';
    closeBtn.style.cssText = 'background:transparent;border:none;color:#fff;font-size:22px;line-height:1;cursor:pointer;padding:0 2px;';
    header.append(title, closeBtn);

    const tabsRow = document.createElement('div');
    tabsRow.style.cssText = 'display:flex;flex-wrap:wrap;gap:4px;padding:10px 12px;flex-shrink:0;border-bottom:1px solid #e2e8f0;';

    const grid = document.createElement('div');
    grid.style.cssText = 'display:grid;grid-template-columns:repeat(auto-fill,minmax(100px,1fr));gap:6px;padding:12px;overflow-y:auto;flex:1;min-height:0;';

    const doInsert = (tex) => {
      insertEquation(editor, tex, editingElement || undefined);
      editingElement = null;
      freeInput.insertBtn.textContent = 'Insert';
      closeModal();
    };

    const freeInput = buildFreeInputRow(doInsert);

    const onPickTemplate = (tex) => {
      freeInput.insertAtCaret(tex);
    };

    let currentCategory = Object.keys(CATEGORIES)[0];

    function showCategory(name) {
      currentCategory = name;
      grid.innerHTML = '';
      const value = CATEGORIES[name];
      if (value.special === 'matrix') {
        grid.appendChild(buildMatrixBuilder(onPickTemplate));
        value.templates.forEach(([tex, label]) =>
          grid.appendChild(buildTemplateButton(tex, label, onPickTemplate))
        );
      } else {
        value.forEach(([tex, label]) =>
          grid.appendChild(buildTemplateButton(tex, label, onPickTemplate))
        );
      }
      [...tabsRow.children].forEach((t) => {
        const active = t.dataset.cat === name;
        t.style.background = active ? ACCENT_LIGHT : '#fff';
        t.style.borderColor = active ? ACCENT : '#e2e8f0';
        t.style.color = active ? ACCENT_HOVER : '#334155';
        t.style.fontWeight = active ? '600' : '400';
      });
      requestAnimationFrame(() => fitTemplateButtons(grid));
    }

    Object.keys(CATEGORIES).forEach((name) => {
      const tabBtn = document.createElement('button');
      tabBtn.type = 'button';
      tabBtn.textContent = name;
      tabBtn.dataset.cat = name;
      tabBtn.style.cssText =
        'padding:5px 11px;font-size:12px;cursor:pointer;border:1px solid #e2e8f0;border-radius:5px;background:#fff;transition:all .15s;';
      tabBtn.addEventListener('click', () => showCategory(name));
      tabsRow.appendChild(tabBtn);
    });

    const legacyNotice = buildLegacyNotice();

    modal.append(header, legacyNotice, freeInput.element, tabsRow, grid);
    overlay.appendChild(modal);
    document.body.appendChild(overlay);

    function openModal() {
      overlay.style.display = 'flex';
      // Render the grid only now that the modal is actually visible --
      // building it earlier (e.g. at plugin init) measures button widths
      // as 0 (hidden ancestor), which made fitTemplateButtons() shrink
      // every equation to its floor scale until a tab switch re-measured
      // it correctly.
      showCategory(currentCategory);
    }
    function closeModal() {
      overlay.style.display = 'none';
      editingElement = null;
      legacyNotice.style.display = 'none';
      freeInput.setValue('');
      freeInput.insertBtn.textContent = 'Insert';
    }

    closeBtn.addEventListener('click', closeModal);
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && overlay.style.display === 'flex') closeModal();
    });

    // ---- Toolbar button: plain ButtonView that just opens the modal ----
    editor.ui.componentFactory.add('templatePanel', (locale) => {
      const button = new ButtonView(locale);
      button.set({ label: 'Math/Chem Equation', withText: true, tooltip: true });
      button.on('execute', () => {
        editingElement = null;
        legacyNotice.style.display = 'none';
        freeInput.setValue('');
        freeInput.insertBtn.textContent = 'Insert';
        openModal();
      });
      return button;
    });

    // Click an EXISTING equation in the document -> reopen the modal,
    // pre-filled, in edit mode. Relies on CKEditor's own widget selection
    // (already correctly tracking the clicked object -- that's what
    // produces the blue outline), rather than manually mapping the raw DOM
    // click target, since KaTeX/MathJax render many inner spans that
    // aren't part of CKEditor's view tree.
    editor.editing.view.document.on('click', () => {
      const selectedElement = editor.model.document.selection.getSelectedElement();
      if (!selectedElement) return;

      if (selectedElement.name === 'mathEquation') {
        editingElement = selectedElement;
        legacyNotice.style.display = 'none';
        freeInput.setValue(selectedElement.getAttribute('latex') || '');
        freeInput.insertBtn.textContent = 'Update';
        openModal();
      } else if (selectedElement.name === 'legacyMathEquation') {
        // Old Wiris/MathType equation -- pre-fill a best-effort LaTeX guess
        // from its stored MathML. Clicking Update replaces it with the new
        // KaTeX widget; until then, the saved HTML is untouched.
        editingElement = selectedElement;
        const mathml = selectedElement.getAttribute('mathml') || '';
        legacyNotice.style.display = 'block';
        freeInput.setValue(guessLatexFromMathML(mathml));
        freeInput.insertBtn.textContent = 'Convert & Update';
        openModal();
      }
    });
  }
}